/**
 * PlanCard — one payment plan on the Payment Plans page (US 4396): what is owed, the next
 * payment, any past-due amount, and Pay. Expands to the full schedule and payment history.
 */
import { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { OathButton } from '../../../shared/OathUI';
import { formatCurrencyWithSymbol } from '../../powerreporting/utils/formatters';
import { formatPlanTerms } from '../../../../services/oathPaymentPlanService';
import PaymentPlanCard from '../../powerreporting/components/PaymentPlanCard';
import PlanScheduleTable from './PlanScheduleTable';
import { formatScheduleDate } from '../utils/scheduleFormat';
import { getPlanPayableTotal } from '../utils/planPayment';

const Figure = ({ label, value, tone }) => (
  <div>
    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
    <p className={`text-lg font-semibold ${tone || 'text-gray-900 dark:text-white'}`}>{value}</p>
  </div>
);

const PlanCard = ({ plan, canPay, onPay, onVoided, allowVoid }) => {
  const [expanded, setExpanded] = useState(false);

  const isPaidOff = plan.isPaidOff || (Number(plan.remainingBalance) || 0) <= 0;
  const pastDue = Number(plan.pastDueAmount) || 0;
  const payable = getPlanPayableTotal(plan);
  const owed = (Number(plan.startingBalance) || 0) + (Number(plan.feesTotal) || 0);
  const percentPaid = owed > 0 ? Math.min(100, Math.max(0, ((Number(plan.totalPaid) || 0) / owed) * 100)) : 0;
  const terms = formatPlanTerms(plan, formatCurrencyWithSymbol);

  return (
    <div className="p-4 sm:p-5 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {plan.name || (plan.typeName ? `${plan.typeName} payment plan` : `Plan ${plan.id}`)}
            </h3>
            {plan.typeName && (
              <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-teal-50 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300">
                {plan.typeName}
              </span>
            )}
            {isPaidOff ? (
              <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                PAID OFF
              </span>
            ) : plan.hasSchedule && pastDue <= 0 ? (
              <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                CURRENT
              </span>
            ) : null}
          </div>
          {terms && <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{terms}</p>}
        </div>

        {!isPaidOff && (
          <div className="flex items-center gap-2">
            {canPay && payable > 0 && (
              <OathButton size="sm" onClick={() => onPay(plan)}>Make a payment</OathButton>
            )}
            {canPay && payable <= 0 && (
              <span className="text-xs text-gray-500 dark:text-gray-400 max-w-[14rem] text-right">
                This plan can't be paid online yet — contact Bankers to pay.
              </span>
            )}
          </div>
        )}
      </div>

      {pastDue > 0 && (
        <div className="mt-3 flex items-center gap-2 p-3 rounded-xl bg-rose-50 dark:bg-rose-900/20 text-sm text-rose-700 dark:text-rose-300">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          <span>
            <strong>{formatCurrencyWithSymbol(pastDue)}</strong> is past due. Pay it to bring this plan current.
          </span>
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Figure label="Total owed" value={formatCurrencyWithSymbol(plan.remainingBalance)} tone="text-primary dark:text-primary-light" />
        <Figure
          label="Next payment"
          value={!isPaidOff && plan.nextDueDate ? formatScheduleDate(plan.nextDueDate) : '—'}
        />
        <Figure
          label="Amount due"
          value={!isPaidOff && plan.hasSchedule ? formatCurrencyWithSymbol(plan.nextDueAmount) : '—'}
          tone={pastDue > 0 ? 'text-rose-600 dark:text-rose-400' : undefined}
        />
        <Figure label="Payments left" value={!isPaidOff && plan.hasSchedule ? plan.installmentsRemaining : '—'} />
      </div>

      <div className="mt-4">
        <div className="h-2 w-full rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
          <div className="h-full rounded-full bg-primary dark:bg-primary-light" style={{ width: `${percentPaid}%` }} />
        </div>
        <div className="mt-1.5 flex justify-between text-xs text-gray-600 dark:text-gray-400">
          <span>{formatCurrencyWithSymbol(plan.totalPaid)} paid</span>
          <span>
            {formatCurrencyWithSymbol(plan.startingBalance)} starting balance
            {Number(plan.feesTotal) > 0 ? ` + ${formatCurrencyWithSymbol(plan.feesTotal)} late fees` : ''}
          </span>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
      >
        {expanded ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
        {expanded ? 'Hide schedule & payments' : 'View schedule & payments'}
      </button>

      {expanded && (
        <div className="mt-3 space-y-5">
          <div>
            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">Payment schedule</p>
            {plan.hasSchedule ? (
              <PlanScheduleTable schedule={plan.schedule || []} />
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No payment schedule has been set up for this plan.</p>
            )}
          </div>
          <PaymentPlanCard plan={plan} compact allowVoid={allowVoid} onVoided={onVoided} />
        </div>
      )}
    </div>
  );
};

export default PlanCard;
